/* MORLI reference product layout. All state is scoped to its section element. */
(() => {
  'use strict';
  if (window.__morliReferenceMotionV2) return;
  window.__morliReferenceMotionV2 = true;

  const route = () => window.Shopify?.routes?.root || '/';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cleanName = value => String(value || '').replace(/\s+/g, ' ').trim();
  const months = allocation => Number(cleanName(allocation?.name).match(/(\d+)\s*months?/i)?.[1] || 0);
  const packCount = variant => Math.max(1, Number(variant?.units) || Number.parseInt(variant?.option2, 10) || 1);
  const motionAllowed = scope => scope.dataset.motion !== 'false' && !reduceMotion();

  // One-shot viewport fades follow the reference's 300ms timing. Content is
  // visible by default; enhancement runs only when its observer is available.
  class MotionEnhancer {
    constructor(scope, reveal = false) {
      this.scope = scope;
      this.events = new AbortController();
      this.details = new Map();
      this.transient = new Set();
      this.revealNodes = [];
      scope.addEventListener('click', event => {
        const summary = event.target.closest('summary');
        if (!summary || !scope.contains(summary) || !motionAllowed(scope) || !summary.parentElement.animate) return;
        if (event.target.closest('a,button,input,select')) return;
        event.preventDefault();
        this.toggleDetails(summary.parentElement, summary);
      }, { signal: this.events.signal });
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.media.addEventListener('change', () => { if (this.media.matches) this.finish(); }, { signal: this.events.signal });
      if (reveal && motionAllowed(scope) && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('mrf-reveal--shown');
            this.observer.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -50px 0px', threshold: 0.04 });
        this.revealNodes = [...scope.querySelectorAll('.mrc__intro,.mrc__review,.mrc__steps li,.mrc__metrics li,.mrc tbody tr')];
        this.revealNodes.forEach((node, index) => {
          if (node.getBoundingClientRect().top < innerHeight - 50 || window.Shopify?.designMode) return;
          node.style.setProperty('--mrf-reveal-delay', `${(index % 4) * 35}ms`);
          node.classList.add('mrf-reveal');
          this.observer.observe(node);
        });
      }
    }

    play(element, frames, options) {
      if (!motionAllowed(this.scope) || !element?.animate) return;
      const animation = element.animate(frames, options);
      this.transient.add(animation);
      const remove = () => this.transient.delete(animation);
      animation.addEventListener('finish', remove, { once: true });
      animation.addEventListener('cancel', remove, { once: true });
      return animation;
    }

    toggleDetails(details, summary) {
      const old = this.details.get(details);
      const opening = old ? !old.opening : !details.open;
      const from = details.getBoundingClientRect().height;
      old?.animation.cancel();
      details.open = true;
      details.style.overflow = 'hidden';
      const to = opening ? details.scrollHeight : summary.getBoundingClientRect().height;
      const animation = details.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 220, easing: 'cubic-bezier(.4,0,.2,1)' });
      this.details.set(details, { animation, opening });
      animation.onfinish = () => {
        if (this.details.get(details)?.animation !== animation) return;
        details.open = opening;
        details.style.removeProperty('overflow');
        this.details.delete(details);
      };
    }

    finish() {
      this.observer?.disconnect();
      this.revealNodes.forEach(node => node.classList.add('mrf-reveal--shown'));
      this.details.forEach(({ animation, opening }, details) => {
        animation.cancel(); details.open = opening; details.style.removeProperty('overflow');
      });
      this.details.clear();
      this.transient.forEach(animation => animation.cancel());
      this.transient.clear();
    }

    destroy() { this.finish(); this.events.abort(); }
  }

  async function requestJSON(url, options = {}, timeout = 20000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...options, credentials: 'same-origin', signal: controller.signal });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch (_) { throw new Error('The store returned an unexpected response. Please refresh.'); }
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : (typeof data.message === 'string' ? data.message : 'Unable to update the cart. Please try again.'));
      return data;
    } finally { clearTimeout(timer); }
  }

  class MorliReferenceHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.querySelector('[data-mrf-data]')) return;
      this.initialized = true;
      this.events = new AbortController();
      this.busy = false;
      this.added = false;
      try {
        this.data = JSON.parse(this.querySelector('[data-mrf-data]').textContent);
        this.variants = this.data.variants || [];
        this.querySelectorAll('[data-variant-id]').forEach(card => {
          const variant = this.variants.find(v => String(v.id) === card.dataset.variantId);
          if (variant) { variant.units = Number(card.dataset.units) || 1; variant.unitLabel = card.dataset.unitLabel === 'bottle' ? 'bottle' : 'pack'; variant.offerLabel = card.querySelector('.mrf__pack-first strong')?.textContent.trim(); }
        });
        this.form = this.querySelector('form');
        this.subscribe = this.querySelector('[data-subscribe]');
        this.frequency = this.querySelector('[data-frequency]');
        this.addButton = this.querySelector('[data-add]');
        this.error = this.querySelector('[data-error]');
        this.moneyFormat = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.data.currency });
        this.selected = this.variants.find(v => String(v.id) === this.querySelector('[data-variant-input]').value) || this.variants[0];
        if (!this.selected) throw new Error('No product variants are configured.');
        const choices = [...this.querySelectorAll('[data-variant-choice]')];
        if (this.data.configuredOffers && !choices.length) throw new Error('The configured offer variant IDs do not belong to this product.');
        if (choices.length && !choices.some(input => input.value === String(this.selected.id))) {
          this.selected = this.variants.find(v => choices[0].value === String(v.id)) || this.selected;
        }
        this.querySelectorAll('[data-flavor]').forEach(input => { input.checked = input.value === this.selected.option1; });
        this.querySelectorAll('[data-size]').forEach(input => { input.checked = input.value === this.selected.option2; });
        this.buildPlans(false);
        const requestedPlan = new URLSearchParams(location.search).get('selling_plan');
        if (requestedPlan && this.selected.allocations.some(a => String(a.id) === requestedPlan)) {
          this.frequency.value = requestedPlan;
          this.subscribe.checked = true;
        }
        this.addEventListener('change', this.onChange.bind(this), { signal: this.events.signal });
        this.addEventListener('click', this.onClick.bind(this), { signal: this.events.signal });
        this.form.addEventListener('submit', this.submit.bind(this), { signal: this.events.signal });
        this.motion = new MotionEnhancer(this);
        this.bindGallery();
        this.bindPageChrome();
        window.addEventListener('pageshow', () => { this.busy = false; this.added = false; this.render(); }, { signal: this.events.signal });
        this.render();
        this.setShipping();
        const sticky = this.querySelector('[data-sticky]');
        if (sticky) {
          sticky.dataset.visible = 'false';
          sticky.inert = true;
          sticky.setAttribute('aria-hidden', 'true');
          sticky.hidden = false;
          const updateSticky = () => {
            this.stickyFrame = null;
            if (!this.isConnected) return;
            const visible = this.addButton.getBoundingClientRect().bottom <= 0;
            sticky.dataset.visible = String(visible);
            sticky.inert = !visible;
            sticky.setAttribute('aria-hidden', String(!visible));
          };
          const scheduleSticky = () => {
            if (!this.stickyFrame) this.stickyFrame = requestAnimationFrame(updateSticky);
          };
          // An observer alone misses fast jumps from below the viewport to
          // above it when the button never intersects during a rendered frame.
          window.addEventListener('scroll', scheduleSticky, { passive: true, signal: this.events.signal });
          window.addEventListener('resize', scheduleSticky, { passive: true, signal: this.events.signal });
          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(scheduleSticky, { threshold: 0 });
            this.observer.observe(this.addButton);
          }
          updateSticky();
          if ('ResizeObserver' in window) {
            this.stickyResize = new ResizeObserver(() => {
              this.style.setProperty('--mrf-sticky-height', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
              document.documentElement.style.setProperty('--mrf-sticky-height', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
            });
            this.stickyResize.observe(sticky);
          }
        }
      } catch (error) {
        const target = this.querySelector('[data-error]');
        if (target) { target.hidden = false; target.textContent = 'Purchase options could not load. Please refresh the page.'; }
        console.error('MORLI reference section:', error);
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.stickyResize?.disconnect();
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.pageHeader?.remove();
      this.pageFooter?.remove();
      if (!document.querySelector('[data-reference-chrome-part]')) document.documentElement.classList.remove('mrf-reference-page');
      this.motion?.destroy();
      this.imageRequest = (this.imageRequest || 0) + 1;
      this.initialized = false;
    }

    money(cents) { return this.moneyFormat.format(Number(cents) / 100); }

    bindPageChrome() {
      if (this.dataset.referenceChrome !== 'true') return;
      if (document.querySelector('.mrf-page-header')) return;
      const headerTemplate = this.querySelector('[data-reference-header]');
      const footerTemplate = this.querySelector('[data-reference-footer]');
      if (!headerTemplate || !footerTemplate) return;
      const main = this.closest('main') || this.parentElement;
      const section = this.closest('.shopify-section');
      this.pageHeader = headerTemplate.content.firstElementChild.cloneNode(true);
      this.pageFooter = footerTemplate.content.firstElementChild.cloneNode(true);
      const existingLogo = document.querySelector('.section-header .header__heading-logo');
      const fallback = this.pageHeader.querySelector('[data-page-logo-fallback]');
      if (fallback && existingLogo) {
        const logo = existingLogo.cloneNode(true);
        logo.removeAttribute('class');
        fallback.replaceWith(logo);
      }
      main.insertBefore(this.pageHeader, section?.parentElement === main ? section : this.parentElement === main ? this : main.firstChild);
      const existingFooter = document.querySelector('#morliFooter,body > footer,.shopify-section-group-footer-group footer');
      const footerSection = existingFooter?.closest('.shopify-section') || existingFooter;
      if (footerSection?.parentElement) footerSection.before(this.pageFooter);
      else if (main === document.body) main.append(this.pageFooter);
      else main.after(this.pageFooter);
      const style = getComputedStyle(this);
      for (const part of [this.pageHeader,this.pageFooter]) {
        part.style.setProperty('--mrf-accent',style.getPropertyValue('--mrf-accent'));
        part.style.setProperty('--mrf-font',style.getPropertyValue('--mrf-font'));
        part.style.setProperty('--mrf-hfont',style.getPropertyValue('--mrf-hfont'));
      }
      this.pageFooter.addEventListener('change', event => {
        if (event.target.matches('[data-page-country]')) event.target.form?.requestSubmit();
      }, { signal:this.events.signal });
      this.pageHeader.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        this.pageHeader.querySelectorAll('details[open]').forEach(details => { details.open=false; details.querySelector('summary')?.focus(); });
      }, { signal:this.events.signal });
      this.pageHeader.addEventListener('click', event => {
        if (event.target.closest('a')) this.pageHeader.querySelectorAll('details[open]').forEach(details => { details.open=false; });
      }, { signal:this.events.signal });
      document.documentElement.classList.add('mrf-reference-page');
    }

    buildPlans(preserve = true) {
      const old = this.allocation();
      const available = [...this.selected.allocations].sort((a,b) => months(a) - months(b));
      this.frequency.replaceChildren();
      available.forEach(a => this.frequency.add(new Option(cleanName(a.name), String(a.id))));
      const preferred = (preserve && old && available.find(a => months(old) ? months(a) === months(old) : cleanName(a.name) === cleanName(old.name))) || available.find(a => months(a) === packCount(this.selected)) || available[0];
      if (preferred) this.frequency.value = String(preferred.id);
      if (!available.length) this.subscribe.checked = false;
      if (this.selected.requiresPlan && available.length) this.subscribe.checked = true;
    }

    allocation(variant = this.selected) {
      if (!variant) return null;
      const available = variant.allocations || [];
      const exact = available.find(a => String(a.id) === this.frequency?.value);
      if (exact) return exact;
      const selectedAllocation = this.selected?.allocations.find(a => String(a.id) === this.frequency?.value);
      return available.find(a => selectedAllocation && (months(a) && months(a) === months(selectedAllocation))) || available.find(a => months(a) === packCount(variant)) || available[0] || null;
    }

    onChange(event) {
      if (this.busy) return;
      const input = event.target;
      if (input.matches('[data-flavor],[data-size]')) {
        const flavor = this.querySelector('[data-flavor]:checked')?.value;
        const size = this.querySelector('[data-size]:checked')?.value;
        const variant = this.variants.find(v => v.option1 === flavor && v.option2 === size);
        if (!variant) return;
        const changedSize = input.matches('[data-size]');
        this.selected = variant;
        this.buildPlans(!changedSize);
        this.showVariantImage();
      } else if (input.matches('[data-variant-choice]')) {
        const variant = this.variants.find(v => String(v.id) === input.value);
        if (!variant) return;
        this.selected = variant;
        this.buildPlans(false);
        this.showVariantImage();
      }
      this.error.hidden = true;
      this.render();
      if (!window.Shopify?.designMode) {
        const url = new URL(location.href);
        url.searchParams.set('variant', this.selected.id);
        if (this.subscribe.checked && this.allocation()) url.searchParams.set('selling_plan', this.allocation().id);
        else url.searchParams.delete('selling_plan');
        history.replaceState(history.state, '', url);
      }
    }

    onClick(event) {
      const thumbnail = event.target.closest('[data-image-src]');
      if (thumbnail) this.showImage(thumbnail);
      const arrow = event.target.closest('[data-gallery-step]');
      if (arrow) this.stepGallery(Number(arrow.dataset.galleryStep));
      if (event.target.closest('[data-sticky-add]')) {
        // The sticky CTA uses exactly the same validated form submission.
        if (!this.addButton.disabled) this.form.requestSubmit(this.addButton);
      }
    }

    bindGallery() {
      const gallery = this.querySelector('.mrf__gallery');
      if (!gallery) return;
      gallery.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.stepGallery(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal: this.events.signal });
      const surface = this.querySelector('.mrf__image-wrap');
      let start = null;
      surface?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.pointerType === 'pen') start = { x: event.clientX, y: event.clientY };
      }, { passive: true, signal: this.events.signal });
      surface?.addEventListener('pointerup', event => {
        if (!start) return;
        const x = event.clientX - start.x, y = event.clientY - start.y;
        start = null;
        if (Math.abs(x) > 40 && Math.abs(x) > Math.abs(y) * 1.3) this.stepGallery(x < 0 ? 1 : -1);
      }, { passive: true, signal: this.events.signal });
      surface?.addEventListener('pointercancel', () => { start = null; }, { signal: this.events.signal });
    }

    stepGallery(step) {
      const thumbnails = [...this.querySelectorAll('[data-image-src]')];
      if (!thumbnails.length) return;
      const index = Math.max(0, thumbnails.findIndex(t => t.getAttribute('aria-pressed') === 'true'));
      this.showImage(thumbnails[(index + step + thumbnails.length) % thumbnails.length]);
    }

    async showImage(thumbnail) {
      const image = this.querySelector('[data-main-image]');
      if (!thumbnail || !image) return;
      const serial = this.imageRequest = (this.imageRequest || 0) + 1;
      const preview = new Image();
      preview.decoding = 'async';
      preview.src = thumbnail.dataset.imageSrc;
      try { await preview.decode(); } catch (_) { if (!preview.naturalWidth) return; }
      if (serial !== this.imageRequest || !this.isConnected) return;
      image.removeAttribute('srcset');
      image.src = thumbnail.dataset.imageSrc;
      image.alt = thumbnail.dataset.imageAlt;
      this.motion?.play(image, [{ opacity: 0.45 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      this.querySelectorAll('[data-image-src]').forEach(t => t.setAttribute('aria-pressed', String(t === thumbnail)));
      const track = this.querySelector('[data-thumbs]');
      if (track) {
        const x = thumbnail.offsetLeft - track.offsetLeft - (track.clientWidth - thumbnail.clientWidth) / 2;
        track.scrollTo({ left: x, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    }

    showVariantImage() {
      if (!this.selected.imageId) return;
      this.showImage([...this.querySelectorAll('[data-image-id]')].find(t => String(t.dataset.imageId) === String(this.selected.imageId)));
    }

    render() {
      const variant = this.selected;
      const allocation = this.allocation();
      const subscribed = this.subscribe.checked && Boolean(allocation);
      const price = subscribed ? allocation.price : variant.price;
      if (this.renderedPrice !== undefined && this.renderedPrice !== price) {
        this.motion?.play(this.querySelector('[data-add-label]'), [{ opacity: 0.4 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
      }
      this.renderedPrice = price;
      this.subscribe.disabled = this.busy || !variant.allocations.length || variant.requiresPlan;
      if (variant.allocations.length) this.subscribe.removeAttribute('aria-describedby');
      else {
        const status = this.querySelector('[data-subscription-unavailable]');
        if (status?.id) this.subscribe.setAttribute('aria-describedby', status.id);
      }
      this.frequency.disabled = this.busy;
      this.querySelectorAll('[data-flavor],[data-size],[data-variant-choice]').forEach(input => { input.disabled = this.busy; });
      this.querySelectorAll('[data-pack-card]').forEach(card => {
        const match = card.dataset.variantId ? this.variants.find(v => String(v.id) === card.dataset.variantId) : this.variants.find(v => v.option1 === variant.option1 && v.option2 === card.dataset.packCard);
        const a = this.allocation(match);
        const input = card.querySelector('[data-size],[data-variant-choice]');
        const valid = match && match.available && (!subscribed || a);
        input.disabled = this.busy || !valid;
        input.checked = match?.id === variant.id;
        const cardPrice = match ? (subscribed && a ? a.price : match.price) : 0;
        card.querySelector('[data-card-price]').textContent = match ? this.money(cardPrice) : 'Unavailable';
        card.querySelector('[data-unit-price]').textContent = match ? `${this.money(cardPrice / packCount(match))} per ${match.unitLabel || 'pack'}` : '';
        const compare = card.querySelector('[data-card-compare]');
        const comparePrice = subscribed && match ? match.price : Number(match?.compare) || 0;
        compare.hidden = !(comparePrice > cardPrice);
        compare.textContent = compare.hidden ? '' : this.money(comparePrice);
        card.querySelector('[data-unavailable]').hidden = Boolean(valid);
        const ribbon = card.querySelector('[data-ribbon]');
        if (ribbon) ribbon.hidden = false;
      });
      const savings = allocation && variant.price > allocation.price ? Math.floor(100 * (variant.price - allocation.price) / variant.price) : 0;
      this.querySelector('[data-subscription-title]').textContent = allocation ? (savings > 0 ? `Save ${savings}% with automatic refills` : 'Subscribe for automatic refills') : `Save ${this.data.subscriptionOfferPercent || 20}% with automatic refills`;
      const subscriptionArea = this.querySelector('[data-subscription-area]');
      subscriptionArea.hidden = false;
      subscriptionArea.dataset.available = String(Boolean(variant.allocations.length));
      const unavailable = this.querySelector('[data-subscription-unavailable]');
      if (unavailable) unavailable.hidden = Boolean(variant.allocations.length);
      this.querySelector('[data-frequency-wrap]').hidden = !subscribed || variant.allocations.length < 2;
      this.querySelector('[data-cadence]').textContent = allocation && months(allocation) !== 1 ? `${cleanName(allocation.name)}. No commitment. Cancel anytime.` : 'Shipped every month. No commitment. Cancel anytime';
      const recurring = this.querySelector('[data-recurring]');
      recurring.hidden = !subscribed;
      if (subscribed) {
        const adjustments = allocation.adjustments || [];
        const schedule = adjustments.length ? adjustments : [{ price: allocation.price, orders: null }];
        recurring.hidden = schedule.length < 2;
        recurring.textContent = schedule.map((a,i) => {
          if (schedule.length === 1) return `${this.money(a.price)} per recurring order.`;
          if (a.orders) return `${this.money(a.price)} for ${i === 0 ? 'the first' : 'the next'} ${a.orders} order${a.orders === 1 ? '' : 's'}.`;
          return `${this.money(a.price)} per recurring order ${i ? 'thereafter' : ''}.`;
        }).join(' ');
      }
      const giftText = subscribed && this.data.addGifts && this.data.giftIds[packCount(variant)] ? ' Includes a free gift bundle with this order.' : '';
      const unit = variant.unitLabel || 'pack';
      this.querySelector('[data-order-summary]').textContent = `${packCount(variant)} ${unit}${packCount(variant) === 1 ? '' : 's'} · ${this.money(price)} total · ${subscribed ? 'Subscription' : 'One-time purchase'}.${giftText}`;
      this.querySelector('[data-variant-input]').value = variant.id;
      const selling = this.querySelector('[data-selling-input]');
      selling.disabled = !subscribed;
      selling.value = subscribed ? allocation.id : '';
      const invalid = !variant.available || (variant.requiresPlan && !subscribed);
      this.addButton.disabled = this.busy || invalid || this.added;
      this.addButton.setAttribute('aria-busy', String(this.busy));
      this.querySelector('[data-add-label]').textContent = this.added ? 'Added — opening cart…' : this.busy ? 'Adding…' : invalid ? 'Sold out' : this.data.buttonLabel;
      const stickyButton = this.querySelector('[data-sticky-add]');
      if (stickyButton) { stickyButton.disabled = this.addButton.disabled; stickyButton.textContent = this.added ? 'Added' : this.busy ? 'Adding…' : invalid ? 'Sold out' : this.data.stickyLabel || this.data.buttonLabel; stickyButton.setAttribute('aria-busy', String(this.busy)); }
      const stickyPrice = this.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.money(price);
      const stickySummary = this.querySelector('[data-sticky-summary]');
      if (stickySummary) stickySummary.textContent = [variant.offerLabel || variant.option1, variant.option2, subscribed ? cleanName(allocation.name) : 'One-time'].filter(v => v && v !== 'Default Title').join(' · ');
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || this.added || this.addButton.disabled) return;
      const allocation = this.allocation();
      const subscribed = this.subscribe.checked;
      if (subscribed && !allocation) { this.fail('Select an available subscription frequency.'); return; }
      const item = { id: this.selected.id, quantity: 1 };
      if (subscribed) item.selling_plan = allocation.id;
      this.busy = true;
      this.error.hidden = true;
      this.render();
      let adding = false;
      try {
        const giftId = this.data.giftIds[packCount(this.selected)];
        const items = [item];
        if (subscribed && this.data.addGifts && /^\d+$/.test(String(giftId || ''))) {
          const [cart, giftResponse] = await Promise.all([
            requestJSON(`${route()}cart.js`),
            requestJSON(`${route()}variants/${giftId}.json`)
          ]);
          const gift = giftResponse.product_variant || giftResponse.variant;
          if (!gift || Number(gift.id) !== Number(giftId) || Number(gift.price) !== 0) throw new Error('The gift offer is unavailable. Please contact us before ordering.');
          if (!cart.items.some(line => Number(line.variant_id || line.id) === Number(giftId))) items.push({ id: Number(giftId), quantity: 1 });
        }
        adding = true;
        await requestJSON(`${route()}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items }) });
        this.added = true;
        this.busy = false;
        this.render();
        this.dispatchEvent(new CustomEvent('morli:cart-added', { bubbles: true, detail: { items } }));
        location.assign(`${route()}${this.data.afterAdd === 'checkout' ? 'checkout' : 'cart'}`);
      } catch (error) {
        this.busy = false;
        this.render();
        const uncertain = adding && (error.name === 'AbortError' || error instanceof TypeError);
        this.fail(uncertain ? 'The request was interrupted. Check your cart before trying again; the product may already have been added.' : error.message);
      }
    }

    fail(message) { this.error.textContent = message; this.error.hidden = false; }

    async setShipping() {
      try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: this.data.timezone || 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
        const dateParts = Object.fromEntries(parts.map(p => [p.type,p.value]));
        const holidays = new Set(String(this.data.holidays || '').split(',').map(s => s.trim()).filter(Boolean));
        const dateAfter = days => {
          const date = new Date(Date.UTC(Number(dateParts.year), Number(dateParts.month)-1, Number(dateParts.day),12));
          while (days > 0) {
            date.setUTCDate(date.getUTCDate()+1);
            if (![0,6].includes(date.getUTCDay()) && !holidays.has(date.toISOString().slice(0,10))) days--;
          }
          return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
        };
        const first = Math.max(1, Number(this.data.minDays) || 1);
        const last = Math.max(first, Number(this.data.maxDays) || 2);
        this.querySelector('[data-dispatch]').textContent = first === last ? dateAfter(first) : `${dateAfter(first)}–${dateAfter(last)}`;
      } catch (_) { /* Keep the 1–2 business-day fallback. */ }
      const country = code => {
        const flag = this.querySelector('[data-country-flag]');
        const message = this.querySelector('[data-country-message]');
        if (code === 'US') {
          message.textContent = 'Free US shipping';
          flag.onload = () => { if (this.isConnected) flag.hidden = false; };
          flag.onerror = () => { flag.hidden = true; };
          flag.src = 'https://flagcdn.com/w40/us.png';
        } else { message.textContent = 'Shipping calculated at checkout'; flag.hidden = true; }
      };
      country(this.data.country);
      if (this.data.ipCountry) {
        try { const data = await requestJSON('https://get.geojs.io/v1/ip/geo.json', {}, 5000); if (this.isConnected) country(String(data.country_code || '').toUpperCase()); } catch (_) { /* Keep Shopify Market fallback. */ }
      }
    }
  }

  class MorliReferenceContent extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.events = new AbortController();
      this.motion = new MotionEnhancer(this, true);
      const timeline = this.querySelector('[data-timeline]');
      if (timeline) {
        const updateTimeline = () => {
          this.timelineFrame = null;
          if (!this.isConnected) return;
          const rect = timeline.getBoundingClientRect();
          const reduced = reduceMotion() || this.dataset.motion === 'false';
          const trigger = innerHeight / 1.5;
          const progress = reduced ? 1 : Math.max(0, Math.min(1, (trigger - rect.top) / Math.max(1, rect.height)));
          const items = [...timeline.querySelectorAll('li')];
          const states = items.map(item => {
            const dot = item.querySelector('.mrc__timeline-dot').getBoundingClientRect();
            return reduced || dot.top + dot.height / 2 <= trigger;
          });
          timeline.dataset.timelineReady = 'true';
          timeline.style.setProperty('--mrc-progress', `${(progress * 100).toFixed(2)}%`);
          items.forEach((item, index) => item.classList.toggle('is-current', states[index]));
        };
        const scheduleTimeline = () => {
          if (!this.timelineFrame) this.timelineFrame = requestAnimationFrame(updateTimeline);
        };
        window.addEventListener('scroll', scheduleTimeline, { passive: true, signal: this.events.signal });
        window.addEventListener('resize', scheduleTimeline, { passive: true, signal: this.events.signal });
        window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', scheduleTimeline, { signal: this.events.signal });
        if ('ResizeObserver' in window) { this.timelineResize = new ResizeObserver(scheduleTimeline); this.timelineResize.observe(timeline); }
        updateTimeline();
      }
      const track = this.querySelector('[data-review-track]');
      const cards = track ? [...track.querySelectorAll('article')] : [];
      const update = () => {
        if (!track) return;
        const end = Math.max(0, track.scrollWidth - track.clientWidth);
        const previous = this.querySelector('[data-review-step="-1"]');
        const next = this.querySelector('[data-review-step="1"]');
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= end - 2;
        // Mobile follows the current card's natural height, avoiding the large
        // empty area below shorter quotes when another card is much taller.
        if (matchMedia('(max-width: 1023px)').matches && cards.length) {
          const left = track.getBoundingClientRect().left;
          const current = cards.reduce((a,b) => Math.abs(a.getBoundingClientRect().left - left) <= Math.abs(b.getBoundingClientRect().left - left) ? a : b);
          const height = Math.ceil(current.getBoundingClientRect().height);
          if (height > 0 && Math.abs(track.getBoundingClientRect().height - height) > 1) track.style.height = `${height}px`;
        } else track.style.removeProperty('height');
      };
      const schedule = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; update(); });
      };
      const move = direction => {
        if (!track) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = (track.querySelector('article')?.getBoundingClientRect().width || track.clientWidth) + gap;
        track.scrollBy({ left: direction * step, behavior: reduceMotion() || this.dataset.motion === 'false' ? 'auto' : 'smooth' });
      };
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-review-step]');
        if (button) move(Number(button.dataset.reviewStep));
      }, { signal: this.events.signal });
      track?.addEventListener('scroll', schedule, { passive: true, signal: this.events.signal });
      track?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal: this.events.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.events.signal });
      track?.addEventListener('load', schedule, { capture: true, signal: this.events.signal });
      if (track && 'ResizeObserver' in window) { this.resizeObserver = new ResizeObserver(schedule); this.resizeObserver.observe(track); cards.forEach(card => this.resizeObserver.observe(card)); }
      update();
    }
    disconnectedCallback() { this.events?.abort(); this.resizeObserver?.disconnect(); this.timelineResize?.disconnect(); this.motion?.destroy(); cancelAnimationFrame(this.scrollFrame); cancelAnimationFrame(this.timelineFrame); this.scrollFrame = null; this.timelineFrame = null; this.connected = false; }
  }
  if (!customElements.get('morli-reference-hero')) customElements.define('morli-reference-hero', MorliReferenceHero);
  if (!customElements.get('morli-reference-content')) customElements.define('morli-reference-content', MorliReferenceContent);
})();
